package com.crowpay.completionResponse

import com.crowpay.AccessInfo
import com.crowpay.FlowPermissions
import com.crowpay.LineItem
import com.crowpay.LineItemCompletionMessage
import com.crowpay.LineItemState
import com.crowpay.isComplete
import com.lightningkite.now
import kotlinx.datetime.Instant
import kotlin.time.Duration.Companion.hours

/**
 * Takes in the completion message history and parses it, calculating what responses are allowed from the
 * next response.
 *
 * Rules Notes (not all rules but some):
 * - The line item has flags for "allowRaise_____". These flags are set by project timers, and are used to notify the system
 *   that the user can respond out-of-turn to raise an issue or such. Usually this is because one party hasn't responded
 *   quickly enough, and this provides a way to escalate the issue when no response comes.
 * - The client is allowed to raise an issue on their second response, first being "Not Ready" (CompletionResponse.ClientResponse)
 * - The client is allowed to file a dispute on their next response after raising an issue
 * */
class CompletionFlowPermissions(
    val lineItem: LineItem,
    val isClient: Boolean,
    lineItemMessages: List<LineItemCompletionMessage>,
    private val preSubstantialCompletion: Instant? = null
): FlowPermissions {
    val inPreSubstantialCompletion get() = preSubstantialCompletion != null

    override val messages = lineItemMessages.sortedBy { it.created }

    val previousResponse = messages.lastOrNull()

    private val previousWasFromClient = previousResponse?.clientMessage ?: true

    val issueRaisedAt: Instant? = messages.firstNotNullOfOrNull { message ->
        if ((message.responseType as? CompletionResponse.ClientResponse)?.raiseIssue == true) message.created
        else null
    }
    override val issueWasRaised get() = issueRaisedAt != null

    val disputeFiledAt: Instant? = messages.firstNotNullOfOrNull {
        if ((it.responseType as? CompletionResponse.ClientResponse)?.fileDispute != null) it.created
        else null
    }
    override val disputeWasFiled get() = disputeFiledAt != null

    override val concernRaised: Boolean get() = messages.any { it.responseType is CompletionResponse.ClientResponse }

    val resolveLaterAcceptedAt = messages.firstNotNullOfOrNull {
        if ((it.responseType as? CompletionResponse.Accept)?.proposal == CompletionResponse.RequestResolveLater) it.created else null
    }
    val inResolveLater get() = resolveLaterAcceptedAt != null && !inPreSubstantialCompletion

    val baseState = baseState(messages, inPreSubstantialCompletion)

    val mustRequestComplete =
        messages.isEmpty() || previousResponse?.responseType == CompletionResponse.KeepWorking

    val mustRequestReviewOfRemedy =
        previousResponse?.responseType
            ?.let { it is CompletionResponse.Accept && it.proposal is CompletionResponse.Remedy } == true

    val clientProposingResolution = !isClient && previousResponse?.responseType is CompletionResponse.ClientResolution

    val awaitingClientResponse =
        lineItem.state < LineItemState.Resolved && lineItem.state > LineItemState.KeepWorking && !previousWasFromClient

    val awaitingContractorResponse =
        (lineItem.state < LineItemState.ResolveLater || (inResolveLater && clientProposingResolution)) &&
                (previousWasFromClient || mustRequestComplete || mustRequestReviewOfRemedy)

    override val awaitingResponse = (isClient && awaitingClientResponse) || (!isClient && awaitingContractorResponse)

    // Can only request more info two times in a row
    val canRequestMoreInfo =
        !isClient && baseState != LineItemState.Disputed && !messages.filter { !it.clientMessage }.takeLast(2).all { it.responseType == CompletionResponse.MoreInfo }

    val mustEscalate = isClient && previousResponse?.responseType?.let {
        it is CompletionResponse.ForceComplete || it == CompletionResponse.RequestResolveLater || it is CompletionResponse.RequestSiteVisit
    } == true

    private val awaitingClientAndClientHasRespondedOnce = awaitingClientResponse && messages.any { it.clientMessage }
    val canRaiseIssue = isClient && lineItem.issue == null &&
            (lineItem.allowRaiseIssue || awaitingClientAndClientHasRespondedOnce) && !inResolveLater

    val mustRaiseIssue = isClient && lineItem.issue == null && mustEscalate

    val mustFileDispute = isClient && lineItem.issue != null && !disputeWasFiled && mustEscalate

    val canFileDispute = isClient && !disputeWasFiled &&
            (lineItem.allowFileDispute || (awaitingClientResponse && issueWasRaised)) && !inResolveLater

    val canLockProject = lineItem.allowLockProject

    private val clientResponses = messages.map { it.responseType }.filterIsInstance<CompletionResponse.ClientResponse>()

    val canProposeResolveLater =
        lineItem.state in setOf(LineItemState.Issue, LineItemState.Disputed) &&
                clientResponses.none { it.fileDispute == DisputeType.ProjectWide } &&
                previousResponse?.responseType != CompletionResponse.RequestResolveLater &&
                !inResolveLater &&
                !inPreSubstantialCompletion

    val clientCanProposeResolution =
        !lineItem.state.isComplete() && (
                (disputeWasFiled && (awaitingResponse || canLockProject || previousResponse?.let { now() - it.created > 24.hours } == true))
                || (inResolveLater && lineItem.state in setOf(LineItemState.ResolveLater, LineItemState.RequestingReview))
        )

    val contractorCanProposeResolution = (inResolveLater && lineItem.state == LineItemState.ResolveLater) || canLockProject

    val canProposeResolution = (isClient && clientCanProposeResolution) || (!isClient && contractorCanProposeResolution)

    private val acceptedProposals = messages.mapNotNull { (it.responseType as? CompletionResponse.Accept)?.proposal }

    val mostRecentProposal: CompletionResponse.NeedsApproval? = messages.asReversed().firstNotNullOfOrNull {
        (it.responseType as? CompletionResponse.NeedsApproval)?.takeUnless { it in acceptedProposals }
    }

    val mostRecentCompletingProposal: CompletionResponse.NeedsApproval? = messages.asReversed().firstNotNullOfOrNull {
        (it.responseType as? CompletionResponse.NeedsApproval)?.takeUnless { proposal ->
            proposal in acceptedProposals ||
                    CompletionResponse.Accept(proposal, AccessInfo.BLANK)
                        .resultantState(baseState(messages, preSubstantialCompletion, it.created))
                        .isComplete()
                        .not()
        }
    }

    val canAccept get() = isClient && mostRecentProposal != null && lineItem.state > LineItemState.InProgress && lineItem.state < LineItemState.Resolved
    val canDeny = disputeWasFiled &&
            isClient != previousWasFromClient &&
            (previousResponse?.responseType is CompletionResponse.NeedsApproval || clientProposingResolution)

    val canRespond = awaitingResponse || canRaiseIssue || mustRaiseIssue || canFileDispute || mustFileDispute || canProposeResolution || lineItem.allowLockProject
    val canRespondOrAccept get() = canRespond || canAccept

    override val completed: Boolean get() = lineItem.complete != null

    companion object {
        fun baseState(
            history: List<LineItemCompletionMessage>,
            inPreSubstantialCompletion: Boolean
        ): LineItemState {
            // Flags to track relevant events in the message history
            var resolveLaterAccepted = false
            var disputeFiled = false
            var issueRaised = false

            // Iterate through the history once, updating flags as needed
            for (msg in history) {
                when (val resp = msg.responseType) {
                    is CompletionResponse.Accept ->
                        // Check if "Resolve Later" was accepted
                        if (resp.proposal == CompletionResponse.RequestResolveLater) resolveLaterAccepted = true
                    is CompletionResponse.ClientResponse -> {
                        // Check if a dispute was filed
                        if (resp.fileDispute != null) disputeFiled = true
                        // Check if an issue was raised
                        if (resp.raiseIssue) issueRaised = true
                    }
                    else -> {}
                }
            }

            // Determine the base state based on the flags and input
            return when {
                // If "Resolve Later" was accepted and not in pre-substantial completion
                resolveLaterAccepted && !inPreSubstantialCompletion -> LineItemState.ResolveLater
                // If a dispute was filed
                disputeFiled -> LineItemState.Disputed
                // If an issue was raised
                issueRaised -> LineItemState.Issue
                // Default state if none of the above
                else -> LineItemState.NotApproved
            }
        }

        fun baseState(
            history: List<LineItemCompletionMessage>,
            inPreSubstantialCompletion: Instant?,
            cutoff: Instant
        ) = baseState(history.filter { it.created <= cutoff }, inPreSubstantialCompletion?.takeIf { it <= cutoff } != null)

        fun currentState(
            history: List<LineItemCompletionMessage>,
            inPreSubstantialCompletion: Instant?,
            at: Instant
        ): LineItemState {
            val filtered = history.filter { it.created <= at }
            val base = baseState(filtered, inPreSubstantialCompletion?.takeIf { it <= at } != null)
            return filtered.lastOrNull()?.responseType?.resultantState(base) ?: LineItemState.InProgress
        }
    }
}